import {createEntityMeta,SyncStatus} from '../offline/entity.js';
import {
  createSmokingItem,
  orderSmokingItems,
  removeSmokingItem,
  updateSmokingItem
} from './item.js';

export const SMOKING_ITEM_ENTITY_TYPE='smoking_item';
export const EDITABLE_ITEM_SESSION_STATUSES=Object.freeze(['draft','planned','preparation','ready']);
const EDITABLE=new Set(EDITABLE_ITEM_SESSION_STATUSES);

function required(value,label){
  const text=String(value||'').trim();
  if(!text)throw new TypeError(label+' is required');
  return text;
}

function assertEditable(status){
  const value=String(status||'').trim().toLowerCase();
  if(!EDITABLE.has(value))throw new Error('SMOKING_ITEMS_SESSION_LOCKED');
  return value;
}

function conflict(row){
  return row?.syncStatus===SyncStatus.CONFLICT;
}

function repositoryRecord(row){
  const copy={...row};
  delete copy.key;
  delete copy.entityType;
  return copy;
}

function domainFromStored(row){
  return createSmokingItem(repositoryRecord(row));
}

function mergeStored(existing,domain,{now}){
  if(existing){
    return {
      ...repositoryRecord(existing),
      ...domain,
      id:existing.id,
      userId:existing.userId,
      sessionId:existing.sessionId
    };
  }
  return {
    ...createEntityMeta({id:domain.id,now,syncStatus:SyncStatus.PENDING_CREATE}),
    ...domain
  };
}

export function createSmokingItemStore({
  repository,
  queue,
  userId,
  now=()=>new Date().toISOString()
}={}){
  if(!repository?.get||!repository?.list||!repository?.put)throw new TypeError('repository with get/list/put is required');
  if(!queue?.enqueue||!queue?.markConflict)throw new TypeError('sync queue is required');
  const owner=required(userId,'userId');

  function assertOwner(row){
    if(row?.userId!==owner)throw new Error('SMOKING_ITEM_OWNER_MISMATCH');
    return row;
  }

  async function list(sessionId,{includeDeleted=false}={}){
    const session=required(sessionId,'sessionId');
    const rows=await repository.list(SMOKING_ITEM_ENTITY_TYPE);
    const owned=(rows||[])
      .filter(row=>row.userId===owner&&row.sessionId===session)
      .filter(row=>includeDeleted||!row.deletedAt);

    const ordered=orderSmokingItems(owned,{includeDeleted});
    const byId=new Map(owned.map(row=>[row.id,row]));
    return Object.freeze(ordered.map(item=>Object.freeze({...byId.get(item.id),...item})));
  }

  async function get(id){
    const row=await repository.get(SMOKING_ITEM_ENTITY_TYPE,required(id,'id'));
    if(!row)return null;
    return Object.freeze(assertOwner(row));
  }

  async function create(sessionId,input={}, {sessionStatus='draft'}={}){
    assertEditable(sessionStatus);
    const session=required(sessionId,'sessionId');
    const existing=await list(session);
    const nextOrder=existing.length
      ?Math.max(...existing.map(item=>Number(item.sortOrder)||0))+1
      :0;
    const domain=createSmokingItem({
      ...input,
      userId:owner,
      sessionId:session,
      sortOrder:input.sortOrder??nextOrder
    });
    const entity=mergeStored(null,domain,{now:now()});
    return queue.enqueue(SMOKING_ITEM_ENTITY_TYPE,entity,'create');
  }

  async function update(id,changes={}, {sessionStatus='draft'}={}){
    assertEditable(sessionStatus);
    const current=await get(id);
    if(!current)throw new Error('SMOKING_ITEM_NOT_FOUND');
    if(conflict(current))throw new Error('SMOKING_ITEM_CONFLICT');
    const domain=updateSmokingItem(domainFromStored(current),changes);
    const entity=mergeStored(current,domain,{now:now()});
    return queue.enqueue(SMOKING_ITEM_ENTITY_TYPE,entity,'update');
  }

  async function remove(id,{sessionStatus='draft',at=now()}={}){
    assertEditable(sessionStatus);
    const current=await get(id);
    if(!current)throw new Error('SMOKING_ITEM_NOT_FOUND');
    if(conflict(current))throw new Error('SMOKING_ITEM_CONFLICT');
    const domain=removeSmokingItem(domainFromStored(current),{at});
    const entity=mergeStored(current,domain,{now:at});
    return queue.enqueue(SMOKING_ITEM_ENTITY_TYPE,entity,'update');
  }

  async function reorder(sessionId,orderedIds,{sessionStatus='draft'}={}){
    assertEditable(sessionStatus);
    const current=await list(sessionId);
    const ids=Array.from(orderedIds||[],value=>required(value,'item id'));
    const currentIds=current.map(item=>item.id);
    if(ids.length!==currentIds.length||new Set(ids).size!==ids.length){
      throw new TypeError('orderedIds must contain every active item exactly once');
    }
    const allowed=new Set(currentIds);
    if(ids.some(id=>!allowed.has(id)))throw new TypeError('orderedIds contain an unknown item');

    const updated=[];
    for(let index=0;index<ids.length;index+=1){
      const item=current.find(row=>row.id===ids[index]);
      if(conflict(item))throw new Error('SMOKING_ITEM_CONFLICT');
      if(item.sortOrder===index){updated.push(item);continue;}
      updated.push(await update(item.id,{sortOrder:index},{sessionStatus}));
    }
    return Object.freeze(updated);
  }

  async function markConflict(id,details){
    const current=await get(id);
    if(!current)throw new Error('SMOKING_ITEM_NOT_FOUND');
    return queue.markConflict(SMOKING_ITEM_ENTITY_TYPE,current,details);
  }

  return Object.freeze({create,get,list,update,remove,reorder,markConflict});
}
